import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';

const initialFormValues = {
  source: 'customer',
  subject: '',
  name: '',
  email: '',
  message: '',
  submitted: false,
  success: false,
}

export const ContactForm = (props: { onSubmit: Function }) => {
  const { onSubmit } = props;
  const { t } = useTranslation('contact_form');
  const theme = useTheme();
  const [values, setValues] = React.useState(initialFormValues);
  const [errors, setErrors] = React.useState({} as any);

  const validate: any = (fieldValues = values) => {
    let temp: any = { ...errors };

    if ('name' in fieldValues) {
      temp.name = fieldValues.name ? '' : t('field_required', 'This field is required.');
    }

    if ('email' in fieldValues) {
      temp.email = fieldValues.email ? '' : t('field_required', 'This field is required.');
      if (fieldValues.email)
        temp.email = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(fieldValues.email)
          ? ''
          : t('field_email_invalid', 'Email is not valid.');
    }

    if ('subject' in fieldValues) {
      temp.subject = fieldValues.subject.length !== 0 ? '' : t('field_required', 'This field is required.');
    }

    if ('message' in fieldValues) {
      temp.message = fieldValues.message.length !== 0 ? '' : t('field_required', 'This field is required.');
    }

    setErrors({
      ...temp,
    });
  };

  const handleInputValue = (e: any) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value,
    });
    validate({ [name]: value });
  };

  const handleSuccess = () => {
    setValues({
      ...initialFormValues,
      submitted: true,
      success: true,
    });
  };

  const handleError = () => {
    setValues({
      ...initialFormValues,
      submitted: true,
      success: false,
    });
  };

  const formIsValid = (fieldValues = values) => {
    return fieldValues.name &&
      fieldValues.email &&
      fieldValues.message &&
      Object.values(errors).every((x) => x === '');
  };

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();
    const isValid = Object.values(errors).every((x) => x === '') && formIsValid();
    if (isValid) {
      try {
        await onSubmit({
          source: values.source,
          subject: values.subject,
          name: values.name,
          email: values.email,
          message: values.message,
        });
        handleSuccess();
      } catch (error) {
        handleError();
      }
    }
  };

  return (
    <>
      {values.success && values.submitted && (
        <Alert severity="success" sx={{ margin: theme.spacing(2, 0) }}>
          {t('success', 'Thanks for reaching out!')}
        </Alert>
      )}
      {!values.success && values.submitted && (
        <Alert severity="error" sx={{ margin: theme.spacing(2, 0) }}>
          {t('error', 'We are currently unable to complete your transaction!')}
        </Alert>
      )}
      {!values.submitted && (
        <form onSubmit={handleFormSubmit}>
          <TextField
            name="name"
            onBlur={handleInputValue}
            onChange={handleInputValue}
            label={t('name', 'Name')}
            fullWidth
            autoComplete="none"
            margin="dense"
            {...(errors['name'] && { error: true, helperText: errors['name'] })}
          />
          <TextField
            name="email"
            onBlur={handleInputValue}
            onChange={handleInputValue}
            label={t('email', 'Email')}
            fullWidth
            autoComplete="none"
            margin="dense"
            {...(errors['email'] && { error: true, helperText: errors['email'] })}
          />
          <TextField
            name="subject"
            onBlur={handleInputValue}
            onChange={handleInputValue}
            label={t('subject', 'Subject')}
            fullWidth
            autoComplete="none"
            margin="dense"
            {...(errors['subject'] && { error: true, helperText: errors['subject'] })}
          />
          <TextField
            name="message"
            onBlur={handleInputValue}
            onChange={handleInputValue}
            label={t('message', 'Message')}
            fullWidth
            multiline
            rows={5}
            autoComplete="none"
            margin="dense"
            {...(errors['message'] && { error: true, helperText: errors['message'] })}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={!formIsValid()}>
            {t('send_message', 'Send')}
          </Button>
        </form>
      )}
    </>
  )
}